import React, { useState, useEffect, useContext } from 'react';
import { CreditCard, Smartphone, Shield, CheckCircle, AlertTriangle } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import { generateUniqueMongoDbID, saveSubscription, sendMailWithNodemailer } from '../services/api';
import { AppConfigurationContext } from '../Contexts/AppConfigContext';
import MyToast from '../components/MyToast';
import MySpinner from '../components/MySpinner';
import './css/PaymentOptions.css';
import Footer from '../components/Footer';
import Menu from '../components/Navbar';

const PaymentOptions = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const offer = location.state?.offer || null;
    const appConfig = useContext(AppConfigurationContext);

    const [user, setUser] = useState(null);
    const [offerData, setOfferData] = useState(null);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [inProgress, setInProgress] = useState(false);
    const [openToast, setOpenToast] = useState({
        open: false,
        msg: '',
        severity: ''
    });

    // Authentification Firebase
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (authUser) => {
            if (authUser) {
                setUser(authUser);
            } else {
                setUser(null);
            }
        });

        return () => unsubscribe();
    }, []);

    // Vérification et parsing de l'offre
    useEffect(() => {
        if (!offer) {
            navigate('/error');
            return;
        } else {
            const tmp = JSON.parse(offer);
            setOfferData(tmp);
        }
    }, [offer, navigate]);

    const navigateToHome = () => {
        navigate('/accueil');
    };

    const handleMobilePayment = () => {
        navigate('/subscription', {
            state: { offer: offer }
        });
    };

    const handleCardPayment = async (provider = "square") => {
        if (inProgress) return;

        setSelectedPayment('card');
        setInProgress(true);

        try {
            const startDate = new Date();
            const paymentID = (await generateUniqueMongoDbID()).data.data;

            // Enregistrement de la souscription
            const subscription = await saveSubscription({
                offerID: offerData._id,
                userID: user.uid,
                startDate: startDate.toISOString(),
                endDate: new Date(startDate.getTime() + parseInt(offerData.duration) * 24 * 60 * 60 * 1000).toISOString(),
                subscriptionStatus: 'PENDING'
            });

            const subscriptionID = subscription.data.data?._id;
            setInProgress(false);

            let link = null;

            // Logique de sélection des liens selon le provider
            if (provider === "square" && appConfig.payment_links) {
                if (offerData.name === "OR") link = appConfig.payment_links.or;
                if (offerData.name === "Argent") link = appConfig.payment_links.argent;
                if (offerData.name === "Bronze") link = appConfig.payment_links.bronze;
            } else if (provider === "paypal" && appConfig.paypal_payment_links) {
                if (offerData.name === "OR") link = appConfig.paypal_payment_links.or;
                if (offerData.name === "Argent") link = appConfig.paypal_payment_links.argent;
                if (offerData.name === "Bronze") link = appConfig.paypal_payment_links.bronze;
            } else if (provider === "stripe" && appConfig.stripe_payment_links) {
                if (offerData.name === "OR") link = appConfig.stripe_payment_links.or;
                if (offerData.name === "Argent") link = appConfig.stripe_payment_links.argent;
                if (offerData.name === "Bronze") link = appConfig.stripe_payment_links.bronze;
            }

            if (link) {
                try {
                    window.open(link, '_blank');
                    navigateToHome();
                } catch (err) {
                    setOpenToast({
                        open: true,
                        msg: "Une erreur s'est produite lors de l'ouverture du lien de paiement. Veuillez nous contacter sur Whatsapp si l'erreur persiste.",
                        severity: 'error'
                    });
                    return;
                }

                sendMailWithNodemailer({
                    from: 'services@exams-tcfcanada.com',
                    to_email: 'examstcfcanada@gmail.com',
                    from_name: 'EXAMS TCF CANADA',
                    subject: `DEBUT DE PAIEMENT AVEC ${provider.toUpperCase()}`,
                    text: `Un utilisateur vient de cliquer sur payer avec carte via ${provider.toUpperCase()} dans l'app web. Les détails sont les suivants:\n\nEmail: ${user.email}\nOffre: ${offerData?.name}\nPaymentID: ${paymentID}\nSubscriptionID: ${subscriptionID}`
                });
            } else {
                setOpenToast({
                    open: true,
                    msg: "Une erreur s'est produite lors au cours de l'opération. Veuillez nous contacter sur Whatsapp si l'erreur persiste.",
                    severity: 'error'
                });
            }
        } catch (error) {
            setInProgress(false);
            setOpenToast({
                open: true,
                msg: "Une erreur s'est produite lors au cours de l'opération. Veuillez nous contacter sur Whatsapp si l'erreur persiste.",
                severity: 'error'
            });
        }
    };

    const paymentProviders = [
        {
            id: 'stripe',
            name: 'Stripe',
            enabled: appConfig?.stripe_payment_links?.enabled,
            icon: '💳',
            description: 'Paiement sécurisé via Stripe'
        },
        {
            id: 'paypal',
            name: 'PayPal',
            enabled: appConfig?.paypal_payment_links?.enabled,
            icon: '🅿️',
            description: 'Paiement sécurisé via PayPal'
        },
        {
            id: 'square',
            name: 'Square',
            enabled: appConfig?.payment_links?.enabled,
            icon: '⬜',
            description: 'Paiement sécurisé via Square'
        }
    ].filter(provider => provider.enabled);

    return (
        <>
            <Menu />

            <div style={{ marginTop: '6rem' }}></div>
            <div className="pmt-options-wrapper">
                <div className="pmt-options-container">
                    {/* Header avec animation */}
                    <div className="pmt-options-header">
                        <h1 className="pmt-options-title">
                            Méthodes de Paiement
                        </h1>
                        <p className="pmt-options-subtitle">
                            Choisissez votre option de paiement préférée
                        </p>
                    </div>

                    {/* Message d'information avec design moderne */}
                    <div className="pmt-options-info-banner">
                        <div className="pmt-options-info-content">
                            <AlertTriangle className="pmt-options-info-icon" />
                            <div>
                                <p className="pmt-options-info-text">
                                    <span className="pmt-options-info-bold">Important :</span> Si vous rencontrez des difficultés lors du paiement,
                                    contactez notre support via <a href="https://wa.me/18192530943" target="_blank" rel="noopener noreferrer" className="pmt-options-link">WhatsApp</a> ou par email (examstcfcanada@gmail.com).
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="pmt-options-content">
                        {/* Option Mobile Money */}
                        <div className="pmt-options-section">
                            <div className="pmt-options-section-inner">
                                <h2 className="pmt-options-section-title">
                                    <Smartphone className="pmt-options-section-icon pmt-options-icon-green" />
                                    Paiement Mobile
                                </h2>

                                <button
                                    onClick={handleMobilePayment}
                                    disabled={inProgress}
                                    className={`pmt-options-card ${selectedPayment === 'mobile' ? 'pmt-options-card-selected-green' : 'pmt-options-card-hover-green'
                                        } ${inProgress ? 'pmt-options-disabled' : ''}`}
                                >
                                    <div className="pmt-options-card-content">
                                        <div className="pmt-options-card-info">
                                            <div className="pmt-options-icon-wrapper pmt-options-icon-bg-green">
                                                <Smartphone className="pmt-options-card-icon" />
                                            </div>
                                            <div className="pmt-options-card-text">
                                                <h3 className="pmt-options-card-title">Mobile Money</h3>
                                                <p className="pmt-options-card-desc">Payez facilement via Mobile Money</p>
                                            </div>
                                        </div>
                                        {selectedPayment === 'mobile' && (
                                            <CheckCircle className="pmt-options-check-icon" />
                                        )}
                                    </div>
                                </button>
                            </div>
                        </div>

                        {/* Section Carte Bancaire */}
                        <div className="pmt-options-section">
                            <div className="pmt-options-section-inner">
                                <h2 className="pmt-options-section-title">
                                    <CreditCard className="pmt-options-section-icon pmt-options-icon-blue" />
                                    Paiement par Carte Bancaire
                                </h2>
                                <p className="pmt-options-section-desc">
                                    Choisissez votre fournisseur de paiement préféré
                                </p>

                                {paymentProviders.length > 0 ? (
                                    <div className="pmt-options-grid">
                                        {paymentProviders.map((provider) => (
                                            <button
                                                key={provider.id}
                                                onClick={() => handleCardPayment(provider.id)}
                                                disabled={inProgress}
                                                className={`pmt-options-provider-card ${selectedPayment === provider.id
                                                    ? 'pmt-options-provider-selected'
                                                    : 'pmt-options-provider-hover'
                                                    } ${inProgress ? 'pmt-options-disabled' : ''}`}
                                            >
                                                <div className="pmt-options-provider-content">
                                                    <div className="pmt-options-provider-icon-wrapper">
                                                        {provider.icon}
                                                    </div>
                                                    <div className="pmt-options-provider-info">
                                                        <h3 className="pmt-options-provider-title">
                                                            {provider.name}
                                                            {selectedPayment === provider.id && (
                                                                <CheckCircle className="pmt-options-provider-check" />
                                                            )}
                                                        </h3>
                                                        <p className="pmt-options-provider-desc">
                                                            {provider.description}
                                                        </p>
                                                    </div>
                                                    <div className="pmt-options-security-badge">
                                                        <Shield className="pmt-options-security-icon" />
                                                        Sécurisé
                                                    </div>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="pmt-options-empty-state">
                                        <CreditCard className="pmt-options-empty-icon" />
                                        <p className="pmt-options-empty-text">Aucun fournisseur de paiement disponible</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Informations de sécurité */}
                        <div className="pmt-options-security-footer">
                            <div className="pmt-options-security-items">
                                <div className="pmt-options-security-item">
                                    <Shield className="pmt-options-security-item-icon" />
                                    Paiements sécurisés SSL
                                </div>
                                <div className="pmt-options-security-item">
                                    <CheckCircle className="pmt-options-security-item-icon" />
                                    Données chiffrées
                                </div>
                                <div className="pmt-options-security-item">
                                    <Shield className="pmt-options-security-item-icon" />
                                    Conforme PCI DSS
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Composants existants */}
                <MyToast
                    openToast={openToast}
                    setOpenToast={setOpenToast}
                />

                <MySpinner loading={inProgress} />
            </div>
            <Footer />
        </>
    );
};

export default PaymentOptions;